import { useAppDispatch } from "../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { classroomSlice, ClassroomStatus, getClassroomHomeworkName, getClassroomStatusName } from "../../../redux/classroom/classroomSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/user/userSlice";
import { hasPermission, ObjectTarget, PermissionId } from "../../../app/permissions";
import { schoolClassroomsSlice, selectSchoolClassrooms } from "../../../redux/school/schoolClassroomsSlice";
import AddIcon from "@mui/icons-material/Add";
import { routeClassroom, routeClassroomNew } from "../../../routes/classroom";
import PrizaAvatarGrid, { PrizaAvatarGridItem } from "../../../features/base/grid/PrizaAvatarGrid";
import { assertThumbnailUrl, getImageProcessorStyleName } from "../../../redux/common";
import { Alert, Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import ObjectDisplay from "./ObjectDisplay";
import BadgedAvatar from "../../../features/base/BadgedAvatar";
import { useState } from "react";

import GridOnIcon from '@mui/icons-material/GridOn';
import DnsIcon from '@mui/icons-material/Dns';
import UserStack from "../../../features/user/UserStack";

interface ContentListProps {
    schoolId: string;
}

const ClassroomsList = ({ schoolId }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const classrooms = useSelector(selectSchoolClassrooms);
    const [table, setTable] = useState(true);

    const list = classrooms.list || [];

    const user = useSelector(selectUser);

    const mapped: PrizaAvatarGridItem[] = list.map((item) => {
        return {
            image: assertThumbnailUrl(item.url),
            title: item.name,
            subtitle: item.year,
            extra: item.classroomStatus !== ClassroomStatus.ENABLED ?
                <Alert sx={{ my: 0, py: 0 }}
                    severity={item.classroomStatus === ClassroomStatus.NOT_READY ? "warning" : "error"}>
                    <Typography variant="caption" align="left">
                        { getClassroomStatusName(item.classroomStatus) }
                    </Typography>
                </Alert>
                : undefined
            ,
            click: () => {
                dispatch(classroomSlice.actions.select(item));
                navigate(routeClassroom(schoolId, item.id, "main"));
            }
        };
    });

    const buttons = [];
    buttons.push({
        label: table ? <DnsIcon /> : <GridOnIcon />,
        click: () => {
            setTable(!table);
        }
    });
    if (hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId)) {
        buttons.push({
            label: <AddIcon />,
            click: () => {
                dispatch(classroomSlice.actions.clean());
                navigate(routeClassroomNew(schoolId));
            }
        });
    }

    const buildContent = !table ? undefined : () => {
        if (list.length == 0) {
            return <Box sx={{ p: 2 }}>
                <Typography variant="body1" component="div">
                    Nenhuma sala de aula associada a esta escola.
                </Typography>
            </Box>;
        }
        const sx = {
            padding: "4px 8px",
        };
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nome</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Ano</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Rotina</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Status</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Imagem</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Lições de Casa</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Professores</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((item) => {
                        return <TableRow key={item.id} hover
                            onClick={() => {
                                dispatch(classroomSlice.actions.select(item));
                                navigate(routeClassroom(schoolId, item.id, "main"));
                            }}>
                            <TableCell sx={sx}>
                                <Stack direction="row" alignItems="center">
                                    <BadgedAvatar
                                        title={item.name}
                                        avatar={assertThumbnailUrl(item.url)}
                                        size="32px"
                                        fontSize="14px"
                                        marginRight={1}
                                    />
                                    {item.name}
                                </Stack>
                            </TableCell>
                            <TableCell sx={sx}>{item.year}</TableCell>
                            <TableCell sx={sx}>
                                <ObjectDisplay objectTarget={ObjectTarget.ROUTINE} targetId={item.routineId} />
                            </TableCell>
                            <TableCell sx={sx}>{getClassroomStatusName(item.classroomStatus)}</TableCell>
                            <TableCell sx={sx}>{getImageProcessorStyleName(item.imageProcessorStyle)}</TableCell>
                            <TableCell sx={sx}>{getClassroomHomeworkName(item.homework)}</TableCell>
                            <TableCell sx={sx}>
                                <UserStack users={item.teachers} />
                            </TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
        </>;
    };

    return <PrizaAvatarGrid list={mapped}
        toolbar={{
            label: "Salas de Aula",
            buttons: buttons
        }}
        state={classrooms.state}
        retry={() => { dispatch(schoolClassroomsSlice.actions.clean()); }}
        buildContent={buildContent}
        messages={{
            empty: "Nenhuma sala de aula associada a esta escola.",
            error: "Erro ao carregar salas de aula."
        }}
    />;

};

export default ClassroomsList;